// Author: Ozy
// Custom Product Options Handler
// Manages price calculation, file uploads, and form validation for custom product options

if (!customElements.get('product-custom-options-manager')) {
  class ProductCustomOptionsManager {
    constructor() {
      this.optionsContainer = document.querySelector('.product-custom-options');
      if (!this.optionsContainer) return;

      this.productForm = this.optionsContainer.closest('product-form') || document.querySelector('product-form');
      if (!this.productForm) return;

      this.priceElements = document.querySelectorAll('.price__container .price-item--regular');
      this.optionsPriceDisplay = this.optionsContainer.querySelector('[data-options-price]');
      this.optionsPriceSummary = this.optionsContainer.querySelector('[data-options-price-summary]');
      
      this.currentOptionsPrice = 0;
      this.originalPrice = null;

      // Store instance reference for product-form.js access
      this.optionsContainer._managerInstance = this;

      // Get shop domain from product-form data attribute or extract from current URL
      this.shopDomain = this.productForm.dataset.shop || '';
      if (!this.shopDomain) {
        // Fallback: extract from current page URL
        const hostname = window.location.hostname;
        if (hostname.includes('.myshopify.com')) {
          this.shopDomain = hostname;
        } else {
          // Try to get from Shopify global object
          this.shopDomain = window.Shop?.domain || window.shop?.domain || '';
        }
      }

      // Draft Orders API URL - SDOAC backend (sdoac.seraphinagrey.com)
      this.draftOrdersApiUrl = window.DRAFT_ORDERS_API_URL || 'https://sdoac.seraphinagrey.com/api/create-draft-order';

      this.init();
    }

    init() {
      this.setupRadioListeners();
      this.setupFileUpload();
      this.setupFormValidation();
      this.calculateInitialPrice();
    }

    setupRadioListeners() {
      const radioInputs = this.optionsContainer.querySelectorAll('input[type="radio"][data-option-type="radio"]');
      
      radioInputs.forEach(input => {
        input.addEventListener('change', (e) => {
          this.handleOptionChange(e.target);
        });
      });
    }

    handleOptionChange(input) {
      const price = parseFloat(input.dataset.price) || 0;
      this.calculateTotalOptionsPrice();
      this.updatePriceDisplay();
    }

    calculateTotalOptionsPrice() {
      let total = 0;
      const radioInputs = this.optionsContainer.querySelectorAll('input[type="radio"][data-option-type="radio"]:checked');
      
      radioInputs.forEach(input => {
        const price = parseFloat(input.dataset.price) || 0;
        total += price;
      });

      this.currentOptionsPrice = total;
      return total;
    }

    calculateInitialPrice() {
      this.calculateTotalOptionsPrice();
      this.updatePriceDisplay();
    }

    updatePriceDisplay() {
      // Update options price summary
      if (this.optionsPriceDisplay) {
        const formattedPrice = this.formatMoney(this.currentOptionsPrice * 100);
        
        if (this.optionsPriceSummary) {
          if (this.currentOptionsPrice > 0) {
            this.optionsPriceDisplay.textContent = `+${formattedPrice}`;
            this.optionsPriceSummary.style.display = 'flex';
          } else {
            this.optionsPriceSummary.style.display = 'none';
          }
        }
      }

      // Update main product price
      this.updateMainPrice();
    }

    updateMainPrice() {
      const priceElement = document.querySelector('.price__container .price-item--regular');
      if (!priceElement) return;

      // Get base product price
      const priceText = priceElement.textContent.trim();
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      if (!priceMatch) return;

      const basePrice = parseFloat(priceMatch[0].replace(/,/g, ''));
      const totalPrice = basePrice + this.currentOptionsPrice;

      // Update display
      const currency = priceText.match(/[$£€¥]/)?.[0] || '$';
      const formattedTotal = this.formatMoney(totalPrice * 100);
      
      // Find the actual price value element
      const priceValueElement = priceElement.querySelector('.price-item__price, dd, .price');
      if (priceValueElement) {
        // Store original if not already stored
        if (!priceValueElement.dataset.originalPrice) {
          priceValueElement.dataset.originalPrice = priceValueElement.innerHTML;
        }

        if (this.currentOptionsPrice > 0) {
          priceValueElement.innerHTML = formattedTotal + ' <span class="caption" style="font-size: 0.85em; opacity: 0.7;">(incl. options)</span>';
        } else {
          priceValueElement.innerHTML = priceValueElement.dataset.originalPrice;
        }
      }
    }

    formatMoney(cents) {
      // Basic money formatting - matches Shopify's format
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    }

    setupFileUpload() {
      const fileInput = this.optionsContainer.querySelector('input[type="file"][data-option-type="file"]');
      if (!fileInput) return;

      const fileNameDisplay = this.optionsContainer.querySelector('[data-file-name]');
      const filePreview = this.optionsContainer.querySelector('[data-file-preview]');
      const removeButton = this.optionsContainer.querySelector('[data-remove-file]');

      fileInput.addEventListener('change', (e) => {
        const file = e.target.files[0];
        
        if (file) {
          // Update file name display
          if (fileNameDisplay) {
            fileNameDisplay.textContent = file.name;
          }

          // Show preview for images
          if (file.type.startsWith('image/') && filePreview) {
            const reader = new FileReader();
            reader.onload = (event) => {
              const img = filePreview.querySelector('img');
              if (img) {
                img.src = event.target.result;
                filePreview.style.display = 'block';
              }
            };
            reader.readAsDataURL(file);
          }
        }
      });

      // Remove file handler
      if (removeButton) {
        removeButton.addEventListener('click', () => {
          fileInput.value = '';
          if (fileNameDisplay) {
            fileNameDisplay.textContent = 'No file chosen';
          }
          if (filePreview) {
            filePreview.style.display = 'none';
          }
        });
      }
    }

    setupFormValidation() {
      if (!this.productForm) return;

      const form = this.productForm.querySelector('form');
      if (!form) return;

      // Add validation before form submission
      // Now allows normal add-to-cart flow - draft order created at checkout
      form.addEventListener('submit', (e) => {
        if (!this.validateOptions()) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        // Form submits normally to add to cart
        // Checkout interceptor will handle draft order creation
      }, true);
    }

    validateOptions() {
      let isValid = true;
      const errors = [];

      // Check required radio buttons
      const requiredRadioGroups = this.optionsContainer.querySelectorAll('fieldset:has(input[type="radio"][required])');
      requiredRadioGroups.forEach(fieldset => {
        const radioButtons = fieldset.querySelectorAll('input[type="radio"]');
        const isChecked = Array.from(radioButtons).some(radio => radio.checked);
        
        if (!isChecked) {
          isValid = false;
          const legend = fieldset.querySelector('legend');
          errors.push(`Please select an option for ${legend ? legend.textContent : 'required field'}`);
        }
      });

      // Check required text fields
      const requiredTextFields = this.optionsContainer.querySelectorAll('textarea[required], input[type="text"][required]');
      requiredTextFields.forEach(field => {
        if (!field.value.trim()) {
          isValid = false;
          const label = this.optionsContainer.querySelector(`label[for="${field.id}"]`);
          errors.push(`Please fill in ${label ? label.textContent : 'required field'}`);
        }
      });

      // Check required file uploads
      const requiredFileFields = this.optionsContainer.querySelectorAll('input[type="file"][required]');
      requiredFileFields.forEach(field => {
        if (!field.files || field.files.length === 0) {
          isValid = false;
          const label = this.optionsContainer.querySelector(`label[for="${field.id}"]`);
          errors.push(`Please upload a file for ${label ? label.textContent : 'required field'}`);
        }
      });

      // Display errors if any
      if (!isValid) {
        alert('Please complete the following:\n\n' + errors.join('\n'));
      }

      return isValid;
    }

    async createDraftOrder(evt) {
      // Prevent default form submission
      if (evt) {
        evt.preventDefault();
        evt.stopPropagation();
      }

      // Validate options first
      if (!this.validateOptions()) {
        return false;
      }

      const form = this.productForm.querySelector('form');
      if (!form) {
        console.error('Product form not found');
        return false;
      }

      const formData = new FormData(form);
      const variantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity') || '1');

      if (!variantId) {
        this.showError('Please select a product variant');
        return false;
      }

      // Get base product price
      const basePrice = this.getBasePrice();
      const optionsPrice = this.currentOptionsPrice;
      const totalPrice = basePrice + optionsPrice;

      // Collect all properties from form
      const properties = {};
      
      // Get radio button selections (with pricing info)
      const radioInputs = this.optionsContainer.querySelectorAll('input[type="radio"][data-option-type="radio"]:checked');
      radioInputs.forEach(input => {
        const propName = input.name.match(/properties\[(.+)\]/)?.[1];
        if (propName) {
          const price = parseFloat(input.dataset.price) || 0;
          properties[propName] = {
            label: input.value,
            price: price
          };
        }
      });

      // Get text field values
      const textFields = this.optionsContainer.querySelectorAll('textarea, input[type="text"]');
      textFields.forEach(field => {
        const propName = field.name.match(/properties\[(.+)\]/)?.[1];
        if (propName && field.value.trim()) {
          properties[propName] = field.value.trim();
        }
      });

      // Get file upload URLs (if any)
      const fileInputs = this.optionsContainer.querySelectorAll('input[type="file"][data-option-type="file"]');
      for (const fileInput of fileInputs) {
        if (fileInput.files && fileInput.files.length > 0) {
          const propName = fileInput.name.match(/properties\[(.+)\]/)?.[1];
          if (propName) {
            // Files need to be uploaded first - for now, we'll handle this in the backend
            // Or use Shopify's native file upload which stores at /uploads/
            // For now, we'll pass the file name and let Shopify handle it
            properties[propName] = fileInput.files[0].name;
          }
        }
      }

      // Show loading state
      const submitButton = this.productForm.querySelector('[type="submit"]');
      if (submitButton) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');
        const spinner = submitButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }

      try {
        // Prepare request data
        const requestData = {
          shop: this.shopDomain,
          variant_id: variantId,
          base_price: basePrice.toFixed(2),
          options_price: optionsPrice.toFixed(2),
          total_price: totalPrice.toFixed(2),
          quantity: quantity,
          properties: properties
        };

        // Call SDOAC API
        const response = await fetch(this.draftOrdersApiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.error || `Server error: ${response.status}`);
        }

        // Redirect to draft order invoice URL
        if (data.invoice_url) {
          window.location.href = data.invoice_url;
        } else {
          throw new Error('No invoice URL returned from server');
        }

      } catch (error) {
        console.error('Draft order creation failed:', error);
        
        // Reset loading state
        if (submitButton) {
          submitButton.removeAttribute('aria-disabled');
          submitButton.classList.remove('loading');
          const spinner = submitButton.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
        }

        // Show error message
        this.showError(error.message || 'Unable to process custom options. Please try again or contact support.');
        
        return false;
      }
    }

    getBasePrice() {
      const priceElement = document.querySelector('.price__container .price-item--regular');
      if (!priceElement) return 0;

      const priceText = priceElement.textContent.trim();
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      if (!priceMatch) return 0;

      return parseFloat(priceMatch[0].replace(/,/g, ''));
    }

    showError(message) {
      const errorWrapper = this.productForm.querySelector('.product-form__error-message-wrapper');
      const errorMessage = this.productForm.querySelector('.product-form__error-message');
      
      if (errorWrapper && errorMessage) {
        errorMessage.textContent = message;
        errorWrapper.removeAttribute('hidden');
        errorWrapper.setAttribute('role', 'alert');
      } else {
        alert(message);
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new ProductCustomOptionsManager();
    });
  } else {
    new ProductCustomOptionsManager();
  }

  // Also initialize on theme editor section updates
  if (Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      new ProductCustomOptionsManager();
    });
  }
}

